import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { motion, useInView } from 'framer-motion'
import { Hourglass } from 'lucide-react'

import { cn } from '@/utils'

import {
  containerVariants,
  DetailDrawerType,
  PeriodMetricKey,
} from '../../constants'
import { useGetApprovedFiles } from '../../hooks'
import { useDashboardStore } from '../../store'
import { MetricCard } from '../MetricCard'

import { PeriodMetricCard } from './PeriodMetricCard'
import { PeriodMetricFilter } from './PeriodMetricFilter'

export const PeriodMetric: React.FC = () => {
  const ref = React.useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px 0px' })

  const { t } = useTranslation()

  const {
    filters: { companyId },
    aggregationPeriod,
    timeRange,
    setDisplayedDrawer,
  } = useDashboardStore()

  const {
    data: { data: [approvedFilesData] = [] } = {},
    isLoading: isLoadingApprovedFiles,
  } = useGetApprovedFiles({
    bucket_type: aggregationPeriod,
    time_range: timeRange,
    company_id: companyId ?? undefined,
  })

  return (
    <div className="space-y-5">
      <div className="flex gap-2 justify-between py-5 border border-solid border-t-0 border-x-0 border-secondary">
        <div className="space-y-0 5">
          <h2 className="m-0 text-lg font-semibold text-primary-900">
            {t('dashboard.periodMetrics.title')}
          </h2>
          <span className="text-sm-text-tertiary-600 font-regular">
            {t('dashboard.periodMetrics.description')}
          </span>
        </div>
        <PeriodMetricFilter />
      </div>
      <motion.div
        ref={ref}
        className="flex flex-col gap-5"
        variants={containerVariants}
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
      >
        <div
          className={cn(
            'space-y-5',
            'lg:flex lg:gap-5 lg:flex-row lg:w-full lg:flex-wrap lg:space-y-0'
          )}
        >
          <PeriodMetricCard periodKey={PeriodMetricKey.REVIEW_TIME_TREND} />
          <PeriodMetricCard periodKey={PeriodMetricKey.RESOLUTION_RATE_TREND} />
        </div>

        <div
          className={cn(
            'space-y-5',
            'lg:flex lg:gap-5 lg:flex-row lg:w-full lg:flex-wrap lg:space-y-0'
          )}
        >
          <PeriodMetricCard
            periodKey={PeriodMetricKey.AVG_DAYS_PER_FILE_TREND}
          />
          <div className={cn('lg:w-1/3')}>
            <MetricCard
              title={t('dashboard.periodMetrics.totalOutputsApproved')}
              icon={Hourglass}
              value={approvedFilesData?.metric}
              onViewAll={() =>
                setDisplayedDrawer(DetailDrawerType.TOTAL_OUTPUTS_APPROVED)
              }
              isLoading={isLoadingApprovedFiles}
            />
          </div>
        </div>
      </motion.div>
    </div>
  )
}
